import { usePhotoShootController } from "@/features/accounts/ocr/hooks/photo-shoot-controller";
import React, { useState } from "react";
import { Camera } from "@/shared/components/photo-shoot-components/camera";
import { ConfirmView } from "@/shared/components/photo-shoot-components/confirm-view";
import { DetailView } from "@/shared/components/photo-shoot-components/detail_view";
import { HeaderOverlay } from "@/shared/components/photo-shoot-components/header";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useOcrAccountsAggregate } from "@/features/accounts/ocr/hooks/use-ocraccounts-aggregate";
import { BasePageModalForCamera } from "@/shared/components/modal/page-modal/base-page-modal-for-camera";
import { OcrCameraAggregateMode } from "@/shared/config/globals"
import { OcrCameraAggregateModeType } from "@/shared/config/globals";
import { OcrProcessStatus } from "@/shared/config/globals";

interface Props {
  ocrAccountId?: string;
  onTapCloseModal: () => void;
  onTapCompleteModal?: () => void;
}

// コンポーネントの引数
export const CameraAggregateModal = (props: Props) => {
  const { 
    ocrAccountId,
    onTapCloseModal,
    onTapCompleteModal,
  } = props;

  const {
    selectedAccountGroup,
    selectedAccount,
    setSelectedAccount,
    setAccounts,
  } = useAccountGroupsContext();

  const {
    uploadFile,
    ocrAccountsAggregate,
  } = useOcrAccountsAggregate();

  // 送信用のファイルリスト
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const {
    imageList,
    addImageList,
    resetImageList,
    deleteImageUrl,
    previousScreen,
    updatePreviousScreen,
    photoShootCount,
    addPhotoShootCount,
    subtractionPhotoShootCount,
    resetPhotoShootCount,
  } = usePhotoShootController({ setSelectedFiles });

  const [mode, setMode] = useState<OcrCameraAggregateModeType>(OcrCameraAggregateMode.Shoot); // shoot, detail, confirm
  const [photoNumber, setPhotoNumber] = useState(0);

  // アップロード開始ボタン押下時処理
  const handleAggregate = async () => {
    const accountId = ocrAccountId || selectedAccount?.mlinkAccountId;
    if (selectedFiles.length > 0 && accountId) {
      // ファイルアップロード
      const uploadRes = await uploadFile(selectedAccountGroup.accountGroupId, accountId, selectedFiles);
      console.log("UploadRes:", uploadRes);
      if (!uploadRes) {
        return;
      }
      // アップロード準備ができたらOCR明細取込開始
      await ocrAccountsAggregate(selectedAccountGroup.accountGroupId, accountId, uploadRes.ocr_process_id);
      // 処理した口座と選択状態の口座が一致する場合のみ，口座情報へprocessIdをセットする
      if (selectedAccount && selectedAccount.mlinkAccountId === accountId) {
        setSelectedAccount({
          ...selectedAccount,
          ocrProcessId: uploadRes.ocr_process_id,
          ocrProcessStatus: OcrProcessStatus.Processing,
        });
      }
      setAccounts(prev => prev.map(acc =>
        acc.mlinkAccountId === accountId && acc.passbookType === "101"
          ? { ...acc, ocrProcessId: uploadRes.ocr_process_id, ocrProcessStatus: OcrProcessStatus.Processing }
          : acc
      ));
    }
    // モーダルを閉じる
    if (onTapCompleteModal) {
      onTapCompleteModal();
    } else {
      onTapCloseModal();
    }
  };

  // 撮影関連モード切替
  const changeStatus = (inputMode: OcrCameraAggregateModeType) => {
    if (inputMode === OcrCameraAggregateMode.Detail) {
      updatePreviousScreen(mode);
    }
    setMode(inputMode);
  };

  const updatePhotoNumber = (num: number) => {
    setPhotoNumber(num);
  };

  return (
    <BasePageModalForCamera
      className="md-photograph"
      handleClose={() => {
        if (onTapCompleteModal) {
          onTapCompleteModal();
        } else {
          onTapCloseModal();
        }
      }}
      hideCloseButton={mode === OcrCameraAggregateMode.Detail}
      headerContent={
        <HeaderOverlay
          onClose={() => {
            if (onTapCompleteModal) {
              onTapCompleteModal();
            } else {
              onTapCloseModal();
            }
          }}
          changeStatus={changeStatus}
          imageList={imageList}
          resetImageList={resetImageList}
          updatePhotoNumber={updatePhotoNumber}
          photoNumber={photoNumber}
          mode={mode}
          deleteImageUrl={deleteImageUrl}
          previousScreen={previousScreen}
          photoShootCount={photoShootCount}
          subtractionPhotoShootCount={subtractionPhotoShootCount}
          resetPhotoShootCount={resetPhotoShootCount}
        />
      }
    >
      {mode === OcrCameraAggregateMode.Shoot ? (
        <Camera
          changeStatus={changeStatus}
          addImageList={addImageList}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
        />
      ) : mode === OcrCameraAggregateMode.Confirm ? (
        <ConfirmView
          changeStatus={changeStatus}
          imageList={imageList}
          addPhotoShootCount={addPhotoShootCount}
          photoShootCount={photoShootCount}
          resetImageList={resetImageList}
          deleteImageUrl={deleteImageUrl}
          resetPhotoShootCount={resetPhotoShootCount}
          handleAggregate={handleAggregate}
        />
      ) : (
        <DetailView
          changeStatus={changeStatus}
          imageList={imageList}
          photoNumber={photoNumber}
        />
      )}
    </BasePageModalForCamera>
  );
};